import { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, fmtDate, Loading } from "@/components/ui-bits";
import { Plus, X, ChevronRight, Users, MessageSquare, Send, Paperclip, Loader2, FileText, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = ["To Do", "In Progress", "Blocked", "Done"];
const NEXT = { "To Do": "In Progress", "In Progress": "Done", "Blocked": "In Progress", "Done": "To Do" };
const COLUMN_STYLE = {
  "To Do": "bg-slate-100 text-slate-700 border-slate-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
  "Blocked": "bg-amber-50 text-amber-700 border-amber-200",
  "Done": "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const PRIORITY_STYLE = {
  Overdue: { card: "!bg-red-600 border-red-700", title: "text-white", ref: "text-red-100", meta: "text-red-100", move: "text-white hover:text-red-200", divider: "border-white/25" },
  Urgent: { card: "!bg-red-500 border-red-600", title: "text-white", ref: "text-red-50", meta: "text-red-50", move: "text-white hover:text-red-100", divider: "border-white/25" },
  High: { card: "!bg-violet-400 border-violet-500", title: "text-white", ref: "text-violet-100", meta: "text-violet-100", move: "text-white hover:text-violet-200", divider: "border-white/25" },
  Medium: { card: "!bg-amber-400 border-amber-500", title: "text-white", ref: "text-amber-50", meta: "text-amber-50", move: "text-white hover:text-amber-100", divider: "border-white/25" },
  Low: { card: "!bg-emerald-500 border-emerald-600", title: "text-white", ref: "text-emerald-50", meta: "text-emerald-50", move: "text-white hover:text-emerald-100", divider: "border-white/25" },
};
const DEFAULT_STYLE = { card: "bg-white border-slate-200", title: "text-slate-800", ref: "text-blue-700", meta: "text-slate-500", move: "text-blue-700 hover:text-blue-800", divider: "border-slate-100" };

export default function Tasks() {
  const [tasks, setTasks] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [assignee, setAssignee] = useState("all");
  const [form, setForm] = useState({ title: "", property_ref: "", assignee: "You", priority: "Medium", due_date: "", job_type: "" });
  const [jobTypes, setJobTypes] = useState([]);
  const [team, setTeam] = useState([]);
  const [properties, setProperties] = useState([]);

  const load = () => api.get("/tasks").then(({ data }) => setTasks(data));
  useEffect(() => {
    load();
    api.get("/job-types").then(({ data }) => setJobTypes(data.job_types)).catch(() => {});
    api.get("/team-members").then(({ data }) => setTeam(data)).catch(() => {});
    api.get("/properties").then(({ data }) => setProperties(data)).catch(() => {});
  }, []);

  const move = async (t) => {
    const status = NEXT[t.status];
    setTasks((prev) => prev.map((x) => x.task_id === t.task_id ? { ...x, status } : x));
    await api.patch(`/tasks/${t.task_id}`, { status });
  };

  const pickJobType = (jt) => {
    const match = jobTypes.find((j) => j.job_type === jt);
    setForm((f) => ({
      ...f,
      job_type: jt,
      title: f.title || jt,
      assignee: match && match.assignee_name ? match.assignee_name : f.assignee,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/tasks", form);
    toast.success("Task created");
    setOpen(false);
    setForm({ title: "", property_ref: "", assignee: "You", priority: "Medium", due_date: "", job_type: "" });
    load();
  };

  if (!tasks) return <Layout title="Tasks"><Loading /></Layout>;

  const assignees = Array.from(new Set(tasks.map((t) => (t.assignee || "").trim()).filter(Boolean))).sort();
  const visible = assignee === "all" ? tasks : tasks.filter((t) => (t.assignee || "").trim() === assignee);

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 fade-up">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-blue-700 mb-2">Workflow</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Tasks</h1>
          <p className="text-slate-500 mt-1 text-sm">Move tasks through your workflow stages</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1 flex items-center gap-1"><Users size={13} /> Assigned to</label>
            <select data-testid="assignee-filter" value={assignee} onChange={(e) => setAssignee(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="all">Everyone ({tasks.length})</option>
              {assignees.map((a) => (
                <option key={a} value={a}>{a} ({tasks.filter((t) => (t.assignee || "").trim() === a).length})</option>
              ))}
            </select>
          </div>
          <button data-testid="add-task-button" onClick={() => setOpen(true)} className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors">
            <Plus size={16} /> New Task
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        {COLUMNS.map((col) => {
          const items = visible.filter((t) => t.status === col);
          return (
            <div key={col} data-testid={`column-${col.replace(/ /g, "-")}`}>
              <div className={`flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-xl border ${COLUMN_STYLE[col] || COLUMN_STYLE["To Do"]}`}>
                <h3 className="text-sm font-semibold">{col}</h3>
                <span className="text-xs font-semibold bg-white/70 px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="space-y-3 min-h-[100px]">
                {items.map((t) => {
                  const s = PRIORITY_STYLE[t.priority] || DEFAULT_STYLE;
                  return (
                    <Card key={t.task_id} onClick={() => setDetail(t)} className={`p-4 fade-up border cursor-pointer hover:shadow-md transition-shadow ${s.card}`} data-testid={`task-${t.task_id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className={`text-sm font-medium leading-snug ${s.title}`}>{t.title}</p>
                        <Badge>{t.priority}</Badge>
                      </div>
                      {(t.property_address || t.property_ref) && <p className={`text-xs mb-1 ${s.ref}`}>{t.property_address || t.property_ref}</p>}
                      <div className={`flex items-center justify-between mt-3 pt-3 border-t ${s.divider}`}>
                        <span className={`text-xs ${s.meta}`}>{t.assignee} · {fmtDate(t.due_date)}</span>
                        <button onClick={(e) => { e.stopPropagation(); move(t); }} data-testid={`move-task-${t.task_id}`} className={`text-xs font-medium flex items-center gap-0.5 ${s.move}`}>
                          {NEXT[t.status]} <ChevronRight size={12} />
                        </button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">New Task</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Job type</label>
                <select data-testid="task-job-type" value={form.job_type} onChange={(e) => pickJobType(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  <option value="">— Select a job type —</option>
                  {jobTypes.map((j) => <option key={j.job_type} value={j.job_type}>{j.job_type}{j.assignee_name ? ` → ${j.assignee_name}` : ""}</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-1">Assignee is set automatically from Settings; you can override it below.</p>
              </div>
              <Inp label="Title" testid="task-title-input" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Property</label>
                  <select data-testid="task-property-select" value={form.property_ref} onChange={(e) => setForm({ ...form, property_ref: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    <option value="">— No property —</option>
                    {properties.map((p) => <option key={p.property_id} value={p.reference}>{p.address}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assignee</label>
                  <select data-testid="task-assignee-select" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    <option value="You">You</option>
                    {team.map((m) => <option key={m.user_id} value={m.name}>{m.name}</option>)}
                    {form.assignee && form.assignee !== "You" && !team.some((m) => m.name === form.assignee) && (
                      <option value={form.assignee}>{form.assignee}</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    {["Low", "Medium", "High", "Urgent"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <Inp label="Due Date" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} />
              </div>
              <button data-testid="submit-task-button" type="submit" className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors">Create Task</button>
            </form>
          </div>
        </div>
      )}
      {detail && (
        <TaskDetailModal task={detail} onClose={() => setDetail(null)}
          onChanged={(updated) => { setDetail(updated); load(); }} />
      )}
    </Layout>
  );
}

function TaskDetailModal({ task, onClose, onChanged }) {
  const [status, setStatus] = useState(task.status);
  const [checklist, setChecklist] = useState(task.checklist || []);
  const s = PRIORITY_STYLE[task.priority] || DEFAULT_STYLE;

  const changeStatus = async (next) => {
    setStatus(next);
    const { data } = await api.patch(`/tasks/${task.task_id}`, { status: next });
    if (data && data.checklist) setChecklist(data.checklist);
    onChanged({ ...task, ...(data || {}), status: next });
  };

  const toggleItem = async (item_id) => {
    setChecklist((cl) => cl.map((i) => i.item_id === item_id ? { ...i, done: !i.done } : i));
    try {
      const { data } = await api.post(`/tasks/${task.task_id}/checklist/${item_id}/toggle`);
      if (data) { setChecklist(data.checklist || []); setStatus(data.status); onChanged(data); }
    } catch { toast.error("Could not update item"); }
  };

  const doneCount = checklist.filter((i) => i.done).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="task-detail-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge>{task.priority}</Badge>
              {(task.property_address || task.property_ref) && <span className="text-xs text-blue-700">{task.property_address || task.property_ref}</span>}
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900">{task.title}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        {task.description && <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{task.description}</p>}
        <p className="text-xs text-slate-400 mt-2">{task.assignee || "Unassigned"} · Due {fmtDate(task.due_date)}</p>

        {checklist.length > 0 && (
          <div className="mt-4" data-testid="task-checklist">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Checklist · {doneCount}/{checklist.length}</p>
            <div className="space-y-1.5">
              {checklist.map((i) => (
                <button key={i.item_id} data-testid={`task-check-${i.item_id}`} onClick={() => toggleItem(i.item_id)}
                  className="w-full flex items-center gap-2.5 text-left px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors">
                  {i.done ? <CheckSquare size={17} className="text-emerald-600 shrink-0" /> : <Square size={17} className="text-slate-300 shrink-0" />}
                  <span className={`text-sm ${i.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{i.text}</span>
                </button>
              ))}
            </div>
            {task.workflow_id && <p className="text-[11px] text-slate-400 mt-2">Marking this task <b>Done</b> completes the stage and creates the next person's task automatically.</p>}
          </div>
        )}

        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1.5">Status</p>
          <div className="flex flex-wrap gap-2">
            {COLUMNS.map((c) => (
              <button key={c} data-testid={`set-status-${c.replace(/ /g, "-")}`} onClick={() => changeStatus(c)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${status === c ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <TaskComments taskId={task.task_id} />
      </div>
    </div>
  );
}

function TaskComments({ taskId }) {
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadComments = () => api.get(`/comments?entity_type=task&entity_id=${taskId}`).then(({ data }) => setComments(data)).catch(() => {});
  const loadAttachments = () => api.get(`/files?entity_type=task&entity_id=${taskId}&bucket=attachments`).then(({ data }) => setAttachments(data)).catch(() => {});
  useEffect(() => { loadComments(); loadAttachments(); /* eslint-disable-next-line */ }, [taskId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/comments`, { entity_type: "task", entity_id: taskId, body: text.trim() });
      setText("");
      await loadComments();
    } catch { toast.error("Could not send comment"); }
    finally { setSending(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "task");
        fd.append("entity_id", taskId);
        fd.append("bucket", "attachments");
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
      await loadAttachments();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="mt-5 pt-5 border-t border-slate-100" data-testid={`task-comments-${taskId}`}>
      <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><MessageSquare size={15} /> Comments & files</p>
      <div className="space-y-2 max-h-52 overflow-y-auto pr-1 mb-3">
        {comments.length === 0 && attachments.length === 0 && (
          <p className="text-xs text-slate-400">No comments yet. Add a note, question, or upload photos/videos.</p>
        )}
        {comments.map((c) => (
          <div key={c.comment_id} data-testid={`task-comment-${c.comment_id}`} className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-medium text-slate-800 text-xs">{c.author_name}</span>
              <Badge>{c.author_role}</Badge>
              <span className="text-[11px] text-slate-400 ml-auto">{fmtDate(c.date)}</span>
            </div>
            <p className="text-slate-700 whitespace-pre-wrap">{c.body}</p>
          </div>
        ))}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {attachments.map((f) => (
              <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`task-file-${f.file_id}`}
                className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center hover:ring-2 hover:ring-blue-500 transition-all">
                {f.category === "photo" ? <img src={fileUrl(f.file_id)} alt="" className="w-full h-full object-cover" />
                  : f.category === "video" ? <video src={fileUrl(f.file_id)} className="w-full h-full object-cover" />
                  : <FileText size={18} className="text-slate-400" />}
              </a>
            ))}
          </div>
        )}
      </div>
      <form onSubmit={send} className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid={`task-comm-file-${taskId}`} onChange={onUpload} />
        <button type="button" data-testid={`task-comm-attach-${taskId}`} onClick={() => fileRef.current?.click()} disabled={uploading}
          className="w-9 h-9 shrink-0 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-60" title="Attach photos or videos">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
        </button>
        <input data-testid={`task-comm-input-${taskId}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…"
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <button type="submit" data-testid={`task-comm-send-${taskId}`} disabled={sending || !text.trim()}
          className="w-9 h-9 shrink-0 rounded-lg bg-blue-700 text-white hover:bg-blue-800 flex items-center justify-center disabled:opacity-50">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </form>
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600" />
    </div>
  );
}
